<template lang="pug">
    .play-box
        template(v-if='!isLoading')
            game-preparation-counter(v-if='prepareForGame && counter < 4' :counter="counter")
            template(v-else)
                game-header(
                    v-if='!prepareForGame && !hideGameHeader'
                    @soundStateChanged="headerKey = !headerKey"
                    :key="headerKey"
                    :time='getTime'
                    :slug="type"
                    :game-type="parameters && parameters.courseType"
                    :is-homework="!!exerciseId"
                )
                div(v-if='!gameFinished && !isNextExampleButtonShown && mainItem')
                    chain(
                        :items='items' 
                        :main-item="mainItem"
                        :help-text="helpText"
                        :is-vertical-screen="isVerticalScreen"
                        @selectItem='processReply'
                    )
                .play-box__end-container(v-if='(gameFinished || isNextExampleButtonShown)')
                    fireworks(
                        v-if='gameFinished && !prepareForGame',
                        :is-mobile='isMobile'
                    )
                    .play-box__reaction-container
                        result-image.play-box__image(status='success')
                    result-buttons(
                        :exercise-id="exerciseId"
                        :games-type="parameters.courseType"
                        :game-name="type"
                        show-restart-button
                        @restartGame="restartGame"
                    )
                        template(slot="mainButton")
                            cs-button(
                                v-if="isNextExampleButtonShown"
                                @click="getExample"
                                v-focus=""
                                color="blue"
                                pattern="primary-outline"
                            )
                                | {{ $i18n.t('trainer.trainer_gamePlayBox_playOn') }}
                        slot
        cs-loader(v-else='')
</template>

<script>
    import Chain from './Chain.vue';
    import AlpariGameApi from '@/api/games/alpariGameApi';
    import { cloneDeep, shuffle, debounce } from 'lodash';
    import playBoxGameMixin from '@/utils/trainer/playBoxGameMixin';
    import GamePreparationCounter from '@/components/trainer/GamePreparationCounter.vue';

    const DEFAULT_VOLUME = 0.6;
    const HELP_TEXT = 'trainer.trainer_gamePlayBox_lookDownAtTheNumbersAndFindExerciseResult';

    function DEFAULT_DATA() {
        return {
            chainOfAttempts: [[]],
            prepareForGame: true,
            isNextExampleButtonShown: false,
            amountOfExamples: 0,
            isAttemptCorrect: false,
            hideGameHeader: false,
            items: [],
            mainItem: null,
            answer: null,
        };
    }

    function delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    export default {
        components: {
            GamePreparationCounter,
            Chain,
        },
        mixins: [playBoxGameMixin],
        data() {
            return DEFAULT_DATA();
        },
        computed: {
            helpText() {
                return this.$i18n.t(HELP_TEXT);
            },
            goodAnswers() {
                let count = 0;
                this.chainOfAttempts.forEach(example => {
                    example.forEach(attempt => (count += attempt.good));
                });
                return count;
            },
            badAnswers() {
                let count = 0;
                this.chainOfAttempts.forEach(example => {
                    example.forEach(attempt => (count += attempt.bad));
                });
                return count;
            },
            currentNumbers() {
                const examples = this.parameters?.examples || [];
                let count = 0;
                for (const example of examples) {
                    count += example.num;
                    if (count > this.amountOfExamples) return example.numbers;
                }
                return null;
            },
            currentOperation() {
                const examples = this.parameters?.examples || [];
                let count = 0;
                for (const example of examples) {
                    count += example.num;
                    if (count > this.amountOfExamples)
                        return example.operations.multiply ? 'multiplication' : 'division';
                }
                return null;
            },
            numOfExamples() {
                const examples = this.parameters?.examples || [];
                return examples.reduce((acc, { num }) => acc + num, 0);
            },
        },
        methods: {
            async fetchData() {
                try {
                    this.prepareForGame = true;
                    await this.getExercise();
                    await this.getExample();
                    this.registerSounds();
                } catch (e) {
                    this.isLoading = false;
                    this.$toasted.error(this.$root.$t('errors.getting_level'));
                    this.$router.push({ name: 'dashboard' });
                }
            },
            beforeGameRestart() {
                Object.assign(this, DEFAULT_DATA());
            },
            async getExample() {
                this.isLoading = true;
                const paramsForRequest = {
                    type: this.currentOperation,
                    numbers: this.currentNumbers,
                };
                try {
                    const { items } = await AlpariGameApi.getItems(paramsForRequest);
                    this.mainItem = items[0];
                    this.answer = items[1];
                    this.items = shuffle(items.slice(1));
                } catch (e) {
                    this.showErrorMessage(e);
                }
                this.isLoading = false;
                if (!this.timer && !this.prepareForGame) {
                    this.setTimer();
                }
                this.isNextExampleButtonShown = false;
            },
            refreshGameStatBeforeSend() {
                this.gameStat.chainOfAttempts = this.chainOfAttempts;
                this.gameStat.attempts = this.goodAnswers + this.badAnswers;
                this.gameStat.answers = this.amountOfExamples;
                this.gameStat.percent = this.calculatePercent();
                this.gameStat.isCompleted = this.gameFinished;
                this.gameStat.executionTime = this.time;
                this.gameStat.isCorrect = true;
                this.gameStat.numOfExamples = this.numOfExamples;
                this.gameStat.parameters = cloneDeep(this.parameters);
            },
            calculatePercent() {
                const total = this.goodAnswers + this.badAnswers;
                if (total > 0) {
                    return parseInt((this.goodAnswers / total) * 100);
                }
                return 0;
            },
            async processReply(selectedItem, clickedCell) {
                const elemOfExampleAttempts = {
                    good: 0,
                    bad: 0,
                };
                if (selectedItem === this.answer) {
                    elemOfExampleAttempts.good++;
                    this.$createjs.Sound.play('clickCorrect');
                    clickedCell.classList.add('playground__item--correct');
                } else {
                    this.$createjs.Sound.play('clickIncorrect');
                    elemOfExampleAttempts.bad++;
                    clickedCell.classList.add('shake');
                    setTimeout(() => {
                        clickedCell.classList.remove('shake');
                    }, 800);
                }
                await delay(300);
                this.chainOfAttempts[this.amountOfExamples].push(elemOfExampleAttempts);
                if (elemOfExampleAttempts.good) {
                    this.amountOfExamples++;
                    this.isAttemptCorrect = true;
                    this.hideGameHeader = true;
                    this.chainOfAttempts.push([]);
                    if (!this.currentOperation) {
                        this.gameDone();
                        return;
                    }
                    this.debouncedSendStat();
                    this.resetData();
                    return;
                }
                this.debouncedSendStat();
                this.isAttemptCorrect = false;
            },
            resetData() {
                this.isNextExampleButtonShown = true;
                this.hideGameHeader = false;
                this.gameStarted = false;
                this.stopTimer();
                this.timer = null;
                this.items = [];
                this.mainItem = null;
                this.answer = null;
            },
            gameDone() {
                this.soundInstance = this.$createjs.Sound.play('gameDone');
                this.soundInstance.volume = DEFAULT_VOLUME;
                this.gameFinished = true;
                this.sendStat();
            },
            startGame() {
                this.gameStarted = true;
                this.showPlayButton = false;
            },
            debouncedSendStat: debounce(function() {
                this.sendStat();
            }, 100),
        },
        watch: {
            counter() {
                if (this.counter === 3) {
                    this.prepareForGame = false;
                    if (!this.timer) {
                        this.setTimer();
                    }
                }
            },
        },
    };
</script>

<style scoped lang="scss">
    @import '@/assets/sass/games/playbox.scss';

    .play-box {
        align-items: flex-start;
        padding: 0 20px;
        box-sizing: border-box;

        &__end-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            align-self: center;
            width: 100%;
        }

        &__reaction-container {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            width: 100%;
        }

        &__image {
            margin-bottom: 15px;
        }
    }
</style>
